import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import $api from 'http/index'
import Notification from 'utils/Notifications'
import { Card, UnvButton, Spinner, Badge, Icon } from 'components'
import ProctorIndicator from './components/ProctorIndicator/ProctorIndicator'
import useProctoring from './hooks/useProctoring'
import s from './TestTakingPage.module.scss'

export default function TestTakingPage() {
  const { t } = useTranslation('common')

  const { id } = useParams()
  const navigate = useNavigate()

  const [test, setTest] = useState(null)
  const [attemptId, setAttemptId] = useState(null)
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [bookmarks, setBookmarks] = useState({})
  const [current, setCurrent] = useState(0)
  const [secondsLeft, setSecondsLeft] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)
  const [notify, setNotify] = useState({ isOpen: false })
  const proctorEnabled =
    !!test?.proctoring_enabled && !!attemptId && !result
  const { active: proctorActive } = useProctoring({
    enabled: proctorEnabled,
    examSessionId: attemptId,
  })
  const startedRef = useRef(false)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const photoTimerRef = useRef(null)
  const screenshotTimerRef = useRef(null)
  const attemptIdRef = useRef(null)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    ;(async () => {
      try {
        const detail = await $api.get(`worktests/${id}/`)
        setTest(detail.data)
      } catch {
        setNotify({ isOpen: true, message: t('auto.testpage_testtakingpage.k1'), type: 'error' })
      }
    })()
  }, [id])

  const sendProctorEvent = (kind, severity = 'warn', payload = {}) => {
    const aid = attemptIdRef.current
    if (!aid) return
    $api
      .post(`worktests/${id}/proctor-event/`, {
        attempt_id: aid,
        kind,
        severity,
        payload,
      })
      .catch(() => {})
  }

  const sendPhoto = async () => {
    const aid = attemptIdRef.current
    const video = videoRef.current
    if (!aid || !video || video.readyState < 2) return
    const w = video.videoWidth || 320
    const h = video.videoHeight || 240
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0, w, h)
    canvas.toBlob(
      (blob) => {
        if (!blob) return
        const fd = new FormData()
        fd.append('attempt_id', String(aid))
        fd.append('photo', blob, `photo_${Date.now()}.jpg`)
        $api
          .post('/worktests/proctoring/photo/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          .catch(() => {})
      },
      'image/jpeg',
      0.7,
    )
  }

  const sendScreenshot = async () => {
    const aid = attemptIdRef.current
    if (!aid) return
    try {
      const w = window.innerWidth
      const h = window.innerHeight
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = '#000'
      ctx.font = '14px sans-serif'
      ctx.fillText(`Screenshot ${new Date().toISOString()}`, 16, 24)
      canvas.toBlob((blob) => {
        if (!blob) return
        const fd = new FormData()
        fd.append('attempt_id', String(aid))
        fd.append('screenshot', blob, `screen_${Date.now()}.png`)
        $api
          .post('/worktests/proctoring/screenshot/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          .catch(() => {})
      }, 'image/png')
    } catch {}
  }

  const start = async () => {
    try {
      const r = await $api.post(`worktests/${id}/start/`)
      const aid = r.data.attempt_id || r.data.id
      setAttemptId(aid)
      attemptIdRef.current = aid
      setQuestions(r.data.questions || r.data.parts || [])
      if (test?.time_limit_sec) setSecondsLeft(test.time_limit_sec)
      if (test?.require_fullscreen) {
        try {
          const el = document.documentElement
          if (el.requestFullscreen) await el.requestFullscreen()
        } catch {}
      }
      if (test?.proctoring_enabled && test?.photo_required) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { width: 320, height: 240 },
            audio: false,
          })
          streamRef.current = stream
          if (videoRef.current) {
            videoRef.current.srcObject = stream
            videoRef.current.play().catch(() => {})
          }
          photoTimerRef.current = setInterval(sendPhoto, 30000)
          setTimeout(sendPhoto, 3000)
        } catch {
          sendProctorEvent('webcam_denied', 'alert')
        }
      }
      if (test?.proctoring_enabled && test?.screenshot_required) {
        screenshotTimerRef.current = setInterval(sendScreenshot, 60000)
      }
    } catch (e) {
      setNotify({
        isOpen: true,
        message: e.response?.data?.detail || 'Не удалось начать тест',
        type: 'error',
      })
    }
  }

  const stopProctoring = () => {
    if (photoTimerRef.current) {
      clearInterval(photoTimerRef.current)
      photoTimerRef.current = null
    }
    if (screenshotTimerRef.current) {
      clearInterval(screenshotTimerRef.current)
      screenshotTimerRef.current = null
    }
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop())
      } catch {}
      streamRef.current = null
    }
  }

  useEffect(() => {
    if (secondsLeft == null || secondsLeft <= 0) return
    const t = setInterval(() => {
      setSecondsLeft((x) => {
        if (x == null) return null
        if (x <= 1) {
          handleSubmit(true)
          return 0
        }
        return x - 1
      })
    }, 1000)
    return () => clearInterval(t)
  }, [secondsLeft != null])

  useEffect(() => {
    if (!attemptId) return
    const onVis = () => {
      if (document.hidden && test?.detect_tab_switch) sendProctorEvent('tab_switch', 'alert')
    }
    const onBlur = () => sendProctorEvent('focus_lost')
    const onPaste = (e) => sendProctorEvent('paste', 'alert', { type: e.type })
    const onCopy = (e) => sendProctorEvent('copy', 'alert', { type: e.type })
    const onFsChange = () => {
      if (test?.require_fullscreen && !document.fullscreenElement)
        sendProctorEvent('fullscreen_exit', 'alert')
    }

    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('blur', onBlur)
    document.addEventListener('paste', onPaste)
    document.addEventListener('copy', onCopy)
    document.addEventListener('fullscreenchange', onFsChange)

    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('blur', onBlur)
      document.removeEventListener('paste', onPaste)
      document.removeEventListener('copy', onCopy)
      document.removeEventListener('fullscreenchange', onFsChange)
    }
  }, [attemptId, id, test])

  useEffect(() => () => stopProctoring(), [])

  const setOption = (qId, optId, multi) => {
    setAnswers((prev) => {
      const cur = prev[qId] || { option_ids: [], free_text: '' }
      const set = new Set(cur.option_ids)
      if (multi) {
        if (set.has(optId)) set.delete(optId)
        else set.add(optId)
      } else {
        set.clear()
        set.add(optId)
      }
      return { ...prev, [qId]: { ...cur, option_ids: Array.from(set) } }
    })
  }

  const setFree = (qId, txt) => {
    setAnswers((prev) => ({ ...prev, [qId]: { ...(prev[qId] || {}), free_text: txt } }))
  }

  const toggleBookmark = (qId) => {
    setBookmarks((b) => ({ ...b, [qId]: !b[qId] }))
  }

  const handleSubmit = async (auto = false) => {
    if (submitting) return
    if (!auto && !window.confirm('Завершить и отправить ответы?')) return
    setSubmitting(true)
    try {
      const payload = {
        attempt_id: attemptId,
        answers: Object.entries(answers).map(([qid, v]) => ({
          part_id: Number(qid),
          option_ids: v.option_ids || [],
          free_text: v.free_text || '',
        })),
      }
      const r = await $api.post(`worktests/${id}/submit/`, payload)
      setResult(r.data)
      stopProctoring()
      try {
        if (document.fullscreenElement) await document.exitFullscreen()
      } catch {}
    } catch (e) {
      setNotify({
        isOpen: true,
        message: e.response?.data?.detail || 'Ошибка отправки',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!test) return (
    <div className={s.page}>
      <Spinner.Center label={t('subjectPage.loading')} />
    </div>
  )

  if (!attemptId) {
    return (
      <div className={s.page}>
        <Card padding="md" className={s.intro}>
          <Card.Header title={test.title} />
          <Card.Body>
            <p>{test.body}</p>
            <ul>
              <li>{t('auto.testtaking.kind', 'Тип')}: <b>{test.kind}</b></li>
              {test.time_limit_sec && (
                <li>{t('auto.testtaking.time_limit', 'Лимит времени')}: <b>{Math.round(test.time_limit_sec / 60)} {t('auto.testtaking.min', 'мин')}</b></li>
              )}
              {test.attempts_allowed && (
                <li>{t('auto.testtaking.attempts', 'Попыток')}: <b>{test.attempts_allowed}</b></li>
              )}
              {test.proctoring_enabled && (
                <li><Badge variant="warning">{t('auto.testpage_testtakingpage.k1')}</Badge> — будут фиксироваться смена вкладки/копирование/выход</li>
              )}
              {test.require_fullscreen && <li>{t('auto.testpage_testtakingpage.k2')}</li>}
              {test.photo_required && <li>{t('auto.testpage_testtakingpage.k3')}</li>}
              {test.screenshot_required && <li>{t('auto.testpage_testtakingpage.k4')}</li>}
            </ul>
          </Card.Body>
          <Card.Footer>
            <UnvButton variant="primary" iconLeft="play" onClick={start}>
              {t('tests.start_test')}
            </UnvButton>
          </Card.Footer>
        </Card>
        <Notification notify={notify} setNotify={setNotify} />
      </div>
    )
  }

  if (result) {
    return (
      <div className={s.page}>
        <Card padding="md" className={s.intro}>
          <Card.Header title={t('tasks.result')} />
          <Card.Body>
            <p>{t('auto.testtaking.points', 'Баллы')}: <b>{result.points} / {result.max_points}</b></p>
            {result.passed !== undefined && (
              <p>
                {t('auto.testtaking.status', 'Статус')}:{' '}
                <Badge variant={result.passed ? 'success' : 'danger'}>
                  {result.passed ? t('assignments.submitted') : t('auto.testpage_testtakingpage.k2')}
                </Badge>
              </p>
            )}
          </Card.Body>
          <Card.Footer>
            <UnvButton variant="ghost" iconLeft="arrow-left" onClick={() => navigate('/tests/')}>
              {t('auto.testtaking.back_to_list', 'К списку тестов')}
            </UnvButton>
          </Card.Footer>
        </Card>
      </div>
    )
  }

  const q = questions[current]
  if (!q) return null
  const opts = q.answer_options || q.options || []
  const multi = !q.one_answer_option_or_multiple_choice
  const ans = answers[q.id] || { option_ids: [], free_text: '' }

  return (
    <div className={s.page}>
      <ProctorIndicator active={proctorActive} />
      <div className={s.bar}>
        <div className={s.title}>{test.title}</div>
        {secondsLeft != null && (
          <div className={`${s.timer} ${secondsLeft < 60 ? s.timerWarn : ''}`}>
            {Math.floor(secondsLeft / 60).toString().padStart(2, '0')}:
            {(secondsLeft % 60).toString().padStart(2, '0')}
          </div>
        )}
      </div>

      <div className={s.qNav}>
        {questions.map((qq, i) => (
          <button
            key={i}
            className={`${s.qNavBtn} ${i === current ? s.active : ''} ${
              answers[qq.id] ? s.answered : ''
            } ${bookmarks[qq.id] ? s.bookmarked : ''}`}
            onClick={() => setCurrent(i)}
          >
            {bookmarks[qq.id] ? <Icon name="star" /> : null}
            {i + 1}
          </button>
        ))}
      </div>

      <div className={s.question}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h3 style={{ flex: 1 }}>
            Вопрос {current + 1} из {questions.length}
          </h3>
          <UnvButton
            variant="ghost"
            size="sm"
            iconLeft={bookmarks[q.id] ? 'star-fill' : 'star'}
            onClick={() => toggleBookmark(q.id)}
          >
            {bookmarks[q.id] ? t('auto.testpage_testtakingpage.k3') : t('auto.testpage_testtakingpage.k4')}
          </UnvButton>
        </div>
        <p className={s.qBody}>{q.body}</p>

        {q.type_answer === 'text' || q.type_answer === 'free' ? (
          <textarea
            className="unv-textarea"
            rows={5}
            value={ans.free_text}
            onChange={(e) => setFree(q.id, e.target.value)}
          />
        ) : (
          <div className={s.options}>
            {opts.map((o) => (
              <label key={o.id} className={s.option}>
                <input
                  type={multi ? 'checkbox' : 'radio'}
                  name={`q${q.id}`}
                  checked={ans.option_ids.includes(o.id)}
                  onChange={() => setOption(q.id, o.id, multi)}
                />
                <span>{o.title || o.text}</span>
              </label>
            ))}
          </div>
        )}

        <div className={s.actions}>
          <UnvButton
            variant="ghost"
            iconLeft="arrow-left"
            disabled={current === 0}
            onClick={() => setCurrent(current - 1)}
          >
            {t('common.back')}
          </UnvButton>
          {current < questions.length - 1 ? (
            <UnvButton variant="primary" iconRight="arrow-right" onClick={() => setCurrent(current + 1)}>
              {t('auto.testtaking.next', 'Далее')}
            </UnvButton>
          ) : (
            <UnvButton variant="success" iconLeft="check" loading={submitting} onClick={() => handleSubmit(false)}>
              {t('auto.testtaking.submit', 'Завершить тест')}
            </UnvButton>
          )}
        </div>
      </div>

      {test.proctoring_enabled && test.photo_required && (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            width: 160,
            height: 120,
            border: '1px solid #ccc',
            borderRadius: 6,
            background: '#000',
          }}
        />
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}
